import { useState, useEffect } from 'react';

export interface Permission {
  id: number;
  resource: string;
  action: string;
  description?: string;
}

export interface UserPermissions {
  is_superuser: boolean;
  organization_id?: string;
  role?: {
    id: string;
    name: string;
    permissions: Permission[];
  };
  has_all_agent_access?: boolean;
  allowed_agent_ids?: string[];
}

export const useCustomPermissions = () => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = () => {
    try {
      const permStr = localStorage.getItem('permissions');
      console.log('[usePermissions] Raw permissions from localStorage:', permStr);
      if (permStr) {
        const userPerms = JSON.parse(permStr);
        console.log('[usePermissions] Parsed permissions:', userPerms);
        console.log('[usePermissions] Role:', userPerms?.role);
        console.log('[usePermissions] Role permissions:', userPerms?.role?.permissions);
        setPermissions(userPerms);
      } else {
        console.warn('[usePermissions] No permissions found in localStorage');
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (resource: string, action: string): boolean => {
    // Super admin has all permissions
    if (permissions?.is_superuser) {
      console.log(`[usePermissions] hasPermission(${resource}, ${action}) = true (super admin)`);
      return true;
    }

    // Check in role permissions
    if (permissions?.role?.permissions) {
      const hasIt = permissions.role.permissions.some(
        (p) => p.resource === resource && p.action === action
      );
      console.log(`[usePermissions] hasPermission(${resource}, ${action}) = ${hasIt}`,
        hasIt ? '✓' : '✗');
      return hasIt;
    }

    console.log(`[usePermissions] hasPermission(${resource}, ${action}) = false (no role/permissions)`);
    return false;
  };

  /**
   * Check if user has ANY of the given permissions
   */
  const hasAnyPermission = (checks: Array<{ resource: string; action: string }>): boolean => {
    return checks.some(({ resource, action }) => hasPermission(resource, action));
  };

  /**
   * Check if user has ALL of the given permissions
   */
  const hasAllPermissions = (checks: Array<{ resource: string; action: string }>): boolean => {
    return checks.every(({ resource, action }) => hasPermission(resource, action));
  };

  /**
   * Check if user is super admin
   */
  const isSuperAdmin = (): boolean => {
    return permissions?.is_superuser === true;
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (roleName: string): boolean => {
    return permissions?.role?.name === roleName;
  };

  /**
   * Check if user is org admin
   */
  const isOrgAdmin = (): boolean => {
    return hasRole('Org Admin');
  };

  /**
   * Check if user is sub admin
   */
  const isSubAdmin = (): boolean => {
    return hasRole('Sub Admin');
  };

  /**
   * Check if user has access to a specific agent
   */
  const hasAgentAccess = (agentId: string): boolean => {
    if (permissions?.is_superuser) return true;
    if (permissions?.has_all_agent_access) return true;
    return permissions?.allowed_agent_ids?.includes(agentId) ?? false;
  };

  /**
   * Get all user permissions
   */
  const getAllPermissions = (): Permission[] => {
    return permissions?.role?.permissions || [];
  };

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    hasRole,
    isOrgAdmin,
    isSubAdmin,
    hasAgentAccess,
    getAllPermissions,
    reload: loadPermissions,
  };
};

// Export alias for PermissionGuard component compatibility
export const usePermissions = useCustomPermissions;